import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Mail, UserPlus, Settings, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const IniciaConBitrix24Page = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeImage, setActiveImage] = useState(null);

  return (
    <div className="min-h-screen bg-white/60">
      <PageHeader />

      <main className="pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto">
          {activeImage && (
            <div
              className="fixed inset-0 z-[60] bg-black/80 p-4 flex items-center justify-center"
              role="dialog"
              aria-modal="true"
              onClick={(e) => { if (e.target === e.currentTarget) setActiveImage(null); }}
            >
              <div className="w-full max-w-6xl">
                <div className="bg-white rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
                  <div className="p-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm text-gray-700 font-medium">{t('inicia_con_bitrix24.preview')}</span>
                    <button
                      type="button"
                      className="text-sm text-gray-600 hover:text-[#141414] transition font-semibold"
                      onClick={() => setActiveImage(null)}
                    >
                      {t('inicia_con_bitrix24.close')}
                    </button>
                  </div>
                  <div className="bg-black">
                    <img
                      src={activeImage.src}
                      alt={activeImage.alt}
                      className="w-full max-h-[80vh] object-contain"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('inicia_con_bitrix24.back')}
          </button>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-[#141414] mb-4">
              {t('inicia_con_bitrix24.title')}
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              {t('inicia_con_bitrix24.subtitle').split('\n').map((line, i) => (
                <React.Fragment key={i}>
                  {line}
                  {i === 0 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>

          <section className="mb-14">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white rounded-2xl p-8 md:p-12 border border-gray-800 shadow-sm">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-2xl md:text-3xl font-bold mb-3">
                  {t('inicia_con_bitrix24.create_account.title')}
                </h2>
                <p className="text-gray-200 mb-8">
                  {t('inicia_con_bitrix24.create_account.desc')}
                </p>
                <a
                  href="https://nextcoders.work/shrt/crearcuentaenbitrix24"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#141414] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition"
                >
                  <ExternalLink size={18} />
                  {t('inicia_con_bitrix24.create_account.cta')}
                </a>
              </div>
            </div>
          </section>

          <section>
            <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 shadow-sm">
              <div className="text-center mb-10">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blue-100 text-blue-700 mb-4">
                  <UserPlus size={26} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#141414] mb-3">
                  {t('inicia_con_bitrix24.invite_nextcoders.title')}
                </h2>
              </div>

              <div className="grid gap-6">
                <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                      <Settings size={20} className="text-gray-700" />
                    </div>
                    <div className="text-gray-700 leading-relaxed">
                      <p className="mb-3">
                        {t('inicia_con_bitrix24.invite_nextcoders.step1')}
                      </p>
                      <p>
                        {t('inicia_con_bitrix24.invite_nextcoders.step1_extra')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6 bg-white">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 size={20} className="text-green-700" />
                    </div>
                    <p className="text-gray-700 leading-relaxed">
                      {t('inicia_con_bitrix24.invite_nextcoders.partners_info')}
                    </p>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6 bg-white">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <UserPlus size={20} className="text-purple-700" />
                    </div>
                    <div className="text-gray-700 leading-relaxed">
                      <p className="mb-4">
                        {t('inicia_con_bitrix24.invite_nextcoders.invite_step')}
                      </p>
                      <button
                        type="button"
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 overflow-hidden hover:border-gray-300 transition cursor-zoom-in"
                        onClick={() => setActiveImage({ src: 'https://helpdesk.bitrix24.es/upload/medialibrary/1cd/30pkm595mmaerk6c2eew7yid9odw99ro/invitar.jpg', alt: t('inicia_con_bitrix24.invite_nextcoders.invite_step') })}
                      >
                        <div className="aspect-[16/9] w-full bg-white">
                          <img
                            src="https://helpdesk.bitrix24.es/upload/medialibrary/1cd/30pkm595mmaerk6c2eew7yid9odw99ro/invitar.jpg"
                            alt={t('inicia_con_bitrix24.invite_nextcoders.invite_step')}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1280x720/f3f4f6/6b7280?text=Imagen+no+disponible"; }}
                          />
                        </div>
                      </button>
                      <p className="mt-4">
                        {t('inicia_con_bitrix24.invite_nextcoders.select_partner')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6 bg-white">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Mail size={20} className="text-amber-700" />
                    </div>
                    <div className="text-gray-700 leading-relaxed">
                      <p className="mb-4">
                        {t('inicia_con_bitrix24.invite_nextcoders.specify_email')}
                      </p>
                      <button
                        type="button"
                        className="w-full rounded-xl border border-gray-200 bg-gray-50 overflow-hidden hover:border-gray-300 transition cursor-zoom-in"
                        onClick={() => setActiveImage({ src: 'https://helpdesk.bitrix24.es/upload/medialibrary/05e/9gy1wxfctl7dlura5h5miecky4m4qzzg/invitar_a_un_socio.jpg', alt: t('inicia_con_bitrix24.invite_nextcoders.specify_email') })}
                      >
                        <div className="aspect-[16/9] w-full bg-white">
                          <img
                            src="https://helpdesk.bitrix24.es/upload/medialibrary/05e/9gy1wxfctl7dlura5h5miecky4m4qzzg/invitar_a_un_socio.jpg"
                            alt={t('inicia_con_bitrix24.invite_nextcoders.specify_email')}
                            className="w-full h-full object-contain"
                            onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/1280x720/f3f4f6/6b7280?text=Imagen+no+disponible"; }}
                          />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </section>

        </div>
      </main>
    </div>
  );
};

export default IniciaConBitrix24Page;
